import '../styles/app.scss';
import '../styles/app-rtl.scss';
import Layout from '../components/layout/Layout';
import { BrowserRouter as Router, Routes, Route, Navigate, BrowserRouter } from 'react-router-dom';
import Providers from './Providers';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Login from './pages/auth/Login';
import ProtectedRoutes from '../utils/ProtectedRoutes';
import ProtectedAuth from '../utils/ProtectedAuth';
import NotFound from './pages/NotFound';
import ChangePasswordPageComponents from './pages/auth/ChangePassword';
import CreateService from './pages/servicesProviders/CreateService';
import ServiceProviders from './pages/servicesProviders/ServiceProviders';
import ServiceDetails from './pages/servicesProviders/ServiceDetails';
import Products from './pages/products/Products';
import CreateOffer from './pages/products/CreateOffer';
import Distributors from './pages/distributors/Distributors';
import CreateDistributor from './pages/distributors/CreateDistributor';
import DistributorDetails from './pages/distributors/DistributorDetails';
import ProductView from './pages/products/ProductView';
import CreateSales from './pages/sales/CreateSales';
import SalesList from './pages/sales/SalesList';
import Applications from './pages/applications/Applications';
import FindDeal from './pages/applications/FindDeal';
import CreateProduct from './pages/products/CreateProduct';
import ApplicationDetails from './pages/applications/ApplicationDetails';
import SalesDetails from './pages/sales/SalesDetails';
import Users from './pages/users/Users';
import CreateUser from './pages/users/CreateUser';
import PermittedRoutes from '../utils/PermittedRoutes';
import RolesList from './pages/roles/RolesList';
import CreateRole from './pages/roles/CreateRole';
import StepsCreation from './pages/steps/StepsCreation';
import Steps from './pages/steps/Steps';
import Notifications from './pages/Notifications/Notifications';
import ProductSteps from './pages/applications/ProductSteps';
import VerifyAdminByMail from './pages/VerifyAdminByMail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import BodyTypes from './pages/bodyTypes/BodyTypes';
import CreateBodytype from './pages/bodyTypes/CreateBodyType';
import CreateVehicleMake from './pages/vehicleMakes/CreateVehicleMake';
import VehicleMakes from './pages/vehicleMakes/VehicleMakes';
import FuelTypes from './pages/fuelTypes/FuelTypes';
import CreateFuelType from './pages/fuelTypes/CreateFuelType';
import Vehicles from './pages/vehicles/Vehicles';
import VehicleModels from './pages/vehicleModels/VehicleModels';
import Locations from './pages/locations/Locations';
import CreateLocation from './pages/locations/CreateLocation';
import SingleVehicle from './pages/vehicles/SingleVehicle';
import VehicleModelEdit from './pages/vehicleModels/VehicleModelEdit';
import VariantsList from './pages/variants/VariantsList';
import CreateVariant from './pages/variants/CreateVariant';
import VariantEdit from './pages/variants/VariantEdit';
import CreatePage from './pages/Pages/CreatePage';
import Pages from './pages/Pages/Pages';
import PageSections from './pages/Pages/PageSections';
import Accessories from './pages/Accessories/Accessories';
import CreateAccessory from './pages/Accessories/CreateAccessory';
import SpecificationGroups from './pages/specificationGroups/SpecificationGroups';
import CreateSpecificationGroup from './pages/specificationGroups/CreateSpecificationGroup';
import Specifications from './pages/specifications/Specifications';
import CreateSpecification from './pages/specifications/CreateSpecification';
import Transmissions from './pages/transmissions/Transmission';
import CreateTransmission from './pages/transmissions/CreateTransmission';
import EditVehicle from './pages/vehicles/EditVehicle';
import CreateLead from './pages/leads/CreateLead';
import Leads from './pages/leads/Leads';
import ExteriorInteriorColors from './pages/exteriorInteriorColors/ExteriorInteriorColors';
import CreateExteriorInteriorColor from './pages/exteriorInteriorColors/CreateExteriorInteriorColor';
import OffersForms from './pages/generalForms/OffersForms';
import ContactUsForms from './pages/generalForms/ContactUsForms';
import NewsLetterForms from './pages/generalForms/NewsLetterForms';
import QuoteForms from './pages/generalForms/QuoteForms';
import Cities from './pages/cities/Cities';
import CreateCity from './pages/cities/CreateCity';
import Admins from './pages/admins/Admins';
import CreateAdmin from './pages/admins/CreateAdmin';
import Orders from './pages/orders/Orders';
import SingleOrder from './pages/orders/SingleOrder';
import AdminLeads from './pages/admins/AdminLeads';
import AdminsLeadsAssign from './pages/admins/AdminsLeadsAssign';
import CreateOrder from './pages/orders/CreateOrder';
import OrderSteps from './pages/orderSteps/OrderSteps';
import OrderStepsCreation from './pages/orderSteps/OrderStepsCreation';
import TermsAndConditions from './pages/termsAndConditions/TermsAndConditions';
import CreateTermsAndConditions from './pages/termsAndConditions/CreateTermsAndConditions';
import FAQs from './pages/faqs/FAQs';
import CreateFAQ from './pages/faqs/CreateFAQ';
import LeadsSalesAssign from './pages/leads/LeadsSalesAssign';
import FinancingApplications from './pages/finaningApplications/FinancingApplications';
import SingleFinancingApplication from './pages/finaningApplications/SingleFinancingApplication';
import Warranties from './pages/warranties/Warranties';
import CreateWarranty from './pages/warranties/CreateWarranty';
import WelcomePage from './pages/WelcomePage';
import CreateDeliveryOption from './pages/deliveryOptions/CreateDeliveryOption';
import DeliveryOptions from './pages/deliveryOptions/DeliveryOptions';
import Dashboard from './pages/Dashboard';

const App: React.FC = () => {
  const { i18n } = useTranslation();

  useEffect(() => {
    document.documentElement.dir = i18n.dir();
    document.documentElement.lang = i18n.language;
  }, [i18n, i18n.language]);

  return (
    <BrowserRouter>
      <Providers>
        <Layout>
          <Routes>
            <Route path="/" element={<ProtectedRoutes />}>
              <Route path="/" element={<Navigate to={'/dashboard'} />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/auth/change-password" element={<ChangePasswordPageComponents />} />

              <Route element={<PermittedRoutes permissions={['service_providers.show']} />}>
                <Route path="/services" element={<ServiceProviders />} />
                <Route path="/services/:id" element={<ServiceDetails />} />
              </Route>

              <Route element={<PermittedRoutes permissions={['service_providers.crud']} />}>
                <Route path="/services/create-service" element={<CreateService />} />
                <Route path="/services/create-service/:id" element={<CreateService />} />
              </Route>

              <Route element={<PermittedRoutes permissions={['products.show']} />}>
                <Route path="/products" element={<Products />} />
                <Route path="/products/:id" element={<ProductView />} />
              </Route>

              <Route element={<PermittedRoutes permissions={['products.edit']} />}>
                <Route path="/products/create-product" element={<CreateProduct />} />
                <Route path="/products/create-product/:id" element={<CreateProduct />} />
              </Route>

              <Route element={<PermittedRoutes permissions={['distributors.show']} />}>
                <Route path="/distributors" element={<Distributors />} />
                <Route path="/distributors/:id" element={<DistributorDetails />} />
              </Route>

              <Route element={<PermittedRoutes permissions={['distributors.edit']} />}>
                <Route path="/distributors/create-distributor" element={<CreateDistributor />} />
                <Route path="/distributors/create-distributor/:id" element={<CreateDistributor />} />
              </Route>

              <Route element={<PermittedRoutes permissions={['users.show']} />}>
                <Route path="/sales" element={<SalesList />} />
                <Route path="/sales/:id" element={<SalesDetails />} />
              </Route>

              <Route element={<PermittedRoutes permissions={['users.edit']} />}>
                <Route path="/sales/create-sales-executive" element={<CreateSales />} />
                <Route path="/sales/create-sales-executive/:id" element={<CreateSales />} />
              </Route>

              <Route element={<PermittedRoutes permissions={['offers.edit']} />}>
                <Route path="/products/:id/create-offer" element={<CreateOffer />} />
                <Route path="/products/:id/create-offer/:offerId" element={<CreateOffer />} />
              </Route>

              <Route element={<PermittedRoutes permissions={['applications.get']} />}>
                <Route path="/applications" element={<Applications />} />
                <Route path="/applications/:id" element={<ApplicationDetails />} />
                <Route path="/application-products/:id" element={<ProductSteps />} />
              </Route>

              <Route element={<PermittedRoutes permissions={['applications.get']} />}>
                <Route path="/applications/create/find-deal" element={<FindDeal />} />
              </Route>

              <Route element={<PermittedRoutes permissions={['roles.show']} />}>
                <Route path="/roles" element={<RolesList />} />
              </Route>

              <Route element={<PermittedRoutes permissions={['roles.edit']} />}>
                <Route path="/roles/create-role" element={<CreateRole />} />
                <Route path="/roles/create-role/:id" element={<CreateRole />} />
              </Route>

              <Route element={<PermittedRoutes permissions={['roles.edit']} />}>
                <Route path="/steps" element={<Steps />} />
                <Route path="/steps/create-step" element={<StepsCreation />} />
                <Route path="/steps/create-step/:id" element={<StepsCreation />} />
              </Route>

              <Route element={<PermittedRoutes permissions={['users.show', 'owned-users.show']} />}>
                <Route path="/users" element={<Users />} />
              </Route>
              <Route element={<PermittedRoutes permissions={['users.edit', 'owned-users.edit']} />}>
                <Route path="/users/create-user" element={<CreateUser />} />
                <Route path="/users/create-user/:id" element={<CreateUser />} />
              </Route>

              <Route element={<PermittedRoutes permissions={['body_types.show']} />}>
                <Route path="/body-types" element={<BodyTypes />} />
              </Route>
              <Route element={<PermittedRoutes permissions={['body_types.edit']} />}>
                <Route path="/body-types/create-body-type" element={<CreateBodytype />} />
                <Route path="/body-types/create-body-type/:id" element={<CreateBodytype />} />
              </Route>

              <Route element={<PermittedRoutes permissions={['transmissions.show']} />}>
                <Route path="/transmissions" element={<Transmissions />} />
              </Route>
              <Route element={<PermittedRoutes permissions={['transmissions.edit']} />}>
                <Route path="/transmissions/create-transmission" element={<CreateTransmission />} />
                <Route path="/transmissions/create-transmission/:id" element={<CreateTransmission />} />
              </Route>

              <Route element={<PermittedRoutes permissions={['specs_groups.show']} />}>
                <Route path='/specification-groups'>
                  <Route path="" element={<SpecificationGroups />} />
                  <Route element={<PermittedRoutes permissions={['specs.show']} />}>
                    <Route path='specifications/:slug' element={<Specifications />} />
                  </Route>
                  <Route element={<PermittedRoutes permissions={['specs.edit']} />}>
                    <Route path="specifications/:slug/create-specification" element={<CreateSpecification />} />
                    <Route path="specifications/:slug/create-specification/:id" element={<CreateSpecification />} />
                  </Route>
                </Route>
              </Route>

              <Route element={<PermittedRoutes permissions={['specs_groups.edit']} />}>
                <Route path="/specification-groups/create-group" element={<CreateSpecificationGroup />} />
                <Route path="/specification-groups/create-group/:id" element={<CreateSpecificationGroup />} />
              </Route>

              <Route element={<PermittedRoutes permissions={['models.show']} />}>
                <Route path="/vehicle-models/:id" element={<VehicleModels />} />
              </Route>

              <Route element={<PermittedRoutes permissions={['models.edit']} />}>
                <Route path="/vehicle-models/:id/model-edit/:modelId" element={<VehicleModelEdit />} />
              </Route>

              <Route element={<PermittedRoutes permissions={['variants.show']} />}>
                <Route path="/vehicle-models/:id/model-variants/:modelId" element={<VariantsList />} />
              </Route>

              <Route path='/vehicle-models/:id/model-variants/:modelId' element={<PermittedRoutes permissions={['variants.edit']} />}>
                <Route path="variant-edit/:variantId" element={<VariantEdit />} />
                <Route path="create-variant" element={<CreateVariant />} />
              </Route>

              <Route element={<PermittedRoutes permissions={['vehicles.show']} />} path='/vehicles'>
                <Route path="" element={<Vehicles />} />
                <Route path=":id" element={<SingleVehicle />} />
              </Route>
              <Route element={<PermittedRoutes permissions={['vehicles.show']} />} path='/used-vehicles'>
                <Route path="" element={<Vehicles used />} />
                <Route path="vehicle-edit/:id" element={<EditVehicle />} />
              </Route>

              <Route element={<PermittedRoutes permissions={['orders.show']} />} path='/orders'>
                <Route path="" element={<Orders />} />
                <Route path="order-details/:id" element={<SingleOrder />} />
                <Route path="create-order" element={<CreateOrder />} />
                <Route path="create-order/:id" element={<CreateOrder />} />
              </Route>

              <Route element={<PermittedRoutes permissions={['orders.show']} />} path='/financing-applications'>
                <Route path="" element={<FinancingApplications />} />
                <Route path=":id" element={<SingleFinancingApplication />} />
              </Route>

              <Route path='/pages'>
                <Route path="" element={<Pages />} />
                <Route path="create-page" element={<CreatePage />} />
                <Route path="create-page/:id" element={<CreatePage />} />
                <Route path="page-sections/:id" element={<PageSections />} />
              </Route>
              <Route element={<PermittedRoutes permissions={['general_forms.show']} />} path='/general-forms'>
                <Route path="offers" element={<OffersForms />} />
                <Route path="contact-us" element={<ContactUsForms />} />
                <Route path="news-letter" element={<NewsLetterForms />} />
                <Route path="quote" element={<QuoteForms />} />
              </Route>


              <Route element={<PermittedRoutes permissions={['makes.show']} />}>
                <Route path='/vehicle-makes' element={<VehicleMakes />} />
              </Route>
              <Route element={<PermittedRoutes permissions={['makes.edit']} />}>
                <Route path="/vehicle-makes/create-vehicle-make" element={<CreateVehicleMake />} />
                <Route path="/vehicle-makes/create-vehicle-make/:id" element={<CreateVehicleMake />} />
              </Route>

              <Route element={<PermittedRoutes permissions={['accessories.show']} />}>
                <Route path="/accessories" element={<Accessories />} />
                <Route path="/warranties" element={<Warranties />} />
              </Route>
              <Route element={<PermittedRoutes permissions={['accessories.edit']} />}>
                <Route path="/accessories/create-accessory" element={<CreateAccessory />} />
                <Route path="/accessories/create-accessory/:id" element={<CreateAccessory />} />

                <Route path="/warranties/create-warranty" element={<CreateWarranty />} />
                <Route path="/warranties/create-warranty/:id" element={<CreateWarranty />} />
              </Route>

              <Route element={<PermittedRoutes permissions={['fuel_types.show']} />}>
                <Route path='/fuel-types' element={<FuelTypes />} />
              </Route>
              <Route element={<PermittedRoutes permissions={['fuel_types.edit']} />}>
                <Route path="/fuel-types/create-fuel-type" element={<CreateFuelType />} />
                <Route path="/fuel-types/create-fuel-type/:id" element={<CreateFuelType />} />
              </Route>

              <Route element={<PermittedRoutes permissions={['leeds.show']} />}>
                <Route path='/leads' element={<Leads />} />
              </Route>
              {/* <Route element={<PermittedRoutes permissions={['leeds.edit']} />}> */}
              <Route path="/leads/create-lead" element={<CreateLead />} />
              <Route path="/leads/create-lead/:id" element={<CreateLead />} />
              {/* </Route> */}
              {/* <Route element={<PermittedRoutes permissions={['leeds.assign_sales_to_leads']} />}>
                <Route path='/leads/assigned-sales/:id' element={<LeadsSalesAssign />} />
              </Route> */}

              <Route element={<PermittedRoutes permissions={['colors.show']} />}>
                <Route path='/exterior-colors' element={<ExteriorInteriorColors />} />
              </Route>
              <Route element={<PermittedRoutes permissions={['colors.edit']} />}>
                <Route path="/exterior-colors/create-exterior-color" element={<CreateExteriorInteriorColor />} />
                <Route path="/exterior-colors/create-exterior-color/:id" element={<CreateExteriorInteriorColor />} />
              </Route>

              <Route element={<PermittedRoutes permissions={['colors.show']} />}>
                <Route path='/interior-colors' element={<ExteriorInteriorColors interior />} />
              </Route>
              <Route element={<PermittedRoutes permissions={['colors.edit']} />}>
                <Route path="/interior-colors/create-interior-color" element={<CreateExteriorInteriorColor interior />} />
                <Route path="/interior-colors/create-interior-color/:id" element={<CreateExteriorInteriorColor interior />} />
              </Route>

              <Route element={<PermittedRoutes permissions={['locations.edit']} />}>
                <Route path='/locations' element={<Locations />} />
                <Route path="/locations/create-location/:id" element={<CreateLocation />} />
                <Route path="/locations/create-location" element={<CreateLocation />} />

                <Route path="/delivery-options" element={<DeliveryOptions />} />
                <Route path="/delivery-options/create-delivery-option" element={<CreateDeliveryOption />} />
                <Route path="/delivery-options/create-delivery-option/:id" element={<CreateDeliveryOption />} />

                <Route path='/cities' element={<Cities />} />
                <Route path="/cities/create-city" element={<CreateCity />} />
                <Route path="/cities/create-city/:id" element={<CreateCity />} />
              </Route>

              {/* <Route element={<PermittedRoutes permissions={['leeds.assign_sales_to_leads']} />}>
                <Route path='/admins/assigned-leads/:id' element={<AdminLeads />} />
                <Route path='/admins/assign-lead/:id' element={<AdminsLeadsAssign />} />
              </Route> */}
              <Route element={<PermittedRoutes permissions={['admins.show']} />}>
                <Route path='/admins' element={<Admins />} />
              </Route>
              <Route element={<PermittedRoutes permissions={['admins.edit']} />}>
                <Route path="/admins/create-admin" element={<CreateAdmin />} />
                <Route path="/admins/create-admin/:id" element={<CreateAdmin />} />
              </Route>

              <Route element={<PermittedRoutes permissions={['order_steps.show']} />}>
                <Route path="/order-steps" element={<OrderSteps />} />
              </Route>

              <Route element={<PermittedRoutes permissions={['order_steps.edit']} />}>
                <Route path="/order-steps/create-order-step" element={<OrderStepsCreation />} />
                <Route path="/order-steps/create-order-step/:id" element={<OrderStepsCreation />} />
              </Route>

              <Route element={<PermittedRoutes permissions={['terms_and_conditions.edit']}/>}>
                <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
                <Route path="/terms-and-conditions/create-terms-and-conditions" element={<CreateTermsAndConditions />} />
                <Route path="/terms-and-conditions/create-terms-and-conditions/:id" element={<CreateTermsAndConditions />} />

                <Route path="/faqs" element={<FAQs />} />
                <Route path="/faqs/create-faq" element={<CreateFAQ/>} />
                <Route path="/faqs/create-faq/:id" element={<CreateFAQ/>} />
              </Route>

              {/* <Route element={<PermittedRoutes permissions={['notifications.edit']} />}> */}
              <Route path="/notifications" element={<Notifications />} />
              {/* </Route> */}

              <Route path="/not-found" element={<NotFound />} />
            </Route>

            <Route element={<ProtectedAuth />}>
              <Route path="/auth/login" element={<Login />} />
            </Route>

            <Route path="*" element={<Navigate to="/not-found" replace />} />
            <Route path='/auth/forgot-password/' element={<ForgotPassword />} />
            <Route path='/auth/reset-password/:id' element={<ResetPassword />} />
            {/* This is Temp Because Some Mail routes May come with languages */}
            <Route path='/:lang/auth/reset-password/:id' element={<ResetPassword />} />

            {/* <Route path='/verify-admin/' element={<VerifyAdmin />} /> Not Needed for now */}
            <Route path='/verify-admin/' element={<VerifyAdminByMail />} />
          </Routes>
        </Layout>
      </Providers>
    </BrowserRouter>
  );
};

export default App;
